import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ScreenHeading from '../../utilities/ScreenHeading/ScreenHeading';
import ScrollService from '../../utilities/ScrollService';
import Animations from '../../utilities/Animations';
import './Testimonial.css';

const testimonials = [
  {
    name: 'Jonathan Straub',
    role: 'Software Engineer · Ricoma International',
    photo: 'img/testimonial/jonathan.jpeg',
    quote:
      "I had the privilege of working alongside Christopher at Ricoma International Corporation, where he excelled as a Software Engineer. Christopher's technical prowess spanning frontend and backend technologies allowed him to build robust web applications from start to finish. What made Christopher truly valuable was his willingness to help teammates and explain complex concepts patiently. His positive attitude and dedication to mentoring fostered an environment of continuous learning within our team.",
  },
  {
    name: 'Sara Castillo',
    role: 'UX/UI Designer · SaaS, Web & App Expert',
    photo: 'img/testimonial/sara.jpeg',
    quote:
      'I had the pleasure of collaborating with Christopher in my current role. His proficiency in tackling diverse software development challenges made him an invaluable asset to our team. As one of our most reliable team members, he consistently provided timely updates and executed development tasks with exceptional precision. I highly endorse Chris to any organization looking for a dynamic and skilled engineer capable of driving growth and innovation.',
  },
  {
    name: 'Gavin Thagard',
    role: 'Copywriter · Ricoma International',
    photo: 'img/testimonial/gavin.jpeg',
    quote:
      "It was a genuine privilege to collaborate with Chris during our tenure at Ricoma International Corporation. Chris's remarkable capacity for problem-solving and meticulous attention to detail were pivotal to our team's achievements. His promptness in communication and unwavering commitment to exceed expectations ensured the timely completion of our projects. Chris's technical acumen and proactive approach make him an outstanding candidate for any Full Stack Developer role. I wholeheartedly endorse Chris and am confident that he will be a valuable asset to any organization.",
  },
  {
    name: 'Mike Alessi',
    role: 'Senior Forward Deployed Engineer',
    photo: 'img/testimonial/mike.webp',
    quote:
      "Chris is one of the most dedicated and diligent teammates I've ever had the pleasure of working with. During our time at Full Stack Academy, I was impressed by Chris's grit and determination — especially when faced with an unfamiliar or difficult problem. He is no stranger to hard work and will always exceed expectations. Chris would be an asset to any team, and his genuine, warm, and welcoming personality is the icing on the cake!",
  },
  {
    name: 'Antonio Dinkins',
    role: 'Cloud Engineer',
    photo: 'img/testimonial/antonio.webp',
    quote:
      'Chris is a consummate professional. He is adept at all aspects of the Software Development Life Cycle. He is proficient with organizing, managing team dynamics, team expectations, and team collaboration, and helps to foster an environment that is inclusive and equitable. Chris has advanced technical and troubleshooting skills and an immense business acumen. I would recommend Chris for any technical position and I believe he would excel in a managerial position.',
  },
  {
    name: 'Michael Orman',
    role: 'AWS Solutions Architect',
    photo: 'img/testimonial/michael.webp',
    quote:
      "Impressive is an understatement when talking about Chris. His ability to communicate technical concepts in a comprehensive manner, as well as apply his knowledge from his prior work experience and combine it with the web development skills he had learned over the course of the 17-week bootcamp was educational and inspiring. Chris' command over his technical skills and knowledge make him an excellent collaborator and an asset for any team of developers.",
  },
];

function Copyright() {
  const [year] = useState(() => new Date().getFullYear());
  return (
    <div className="copyright">
      <p style={{ color: 'gray', textAlign: 'center', fontSize: '0.875rem' }}>
        {'Copyright © '}
        {year} Chris Bolosan
      </p>
    </div>
  );
}

const COUNT = testimonials.length;
const AUTOPLAY_DELAY = 5000;

// Wrap offset so there's always a photo on each side regardless of activeIndex
function wrapOffset(raw, n) {
  let o = ((raw % n) + n) % n;
  if (o > Math.floor(n / 2)) o -= n;
  return o;
}

export default function Testimonials(props) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isPaused, setIsPaused] = useState(false);
  const touchStartX = useRef(0);

  let fadeInScreenHandler = (screen) => {
    if (screen.fadeInScreen !== props.id) return;
    Animations.animations.fadeInScreen(props.id);
  };
  ScrollService.currentScreenFadeIn.subscribe(fadeInScreenHandler);

  const next = useCallback(() => {
    setDirection(1);
    setActiveIndex((i) => (i + 1) % COUNT);
  }, []);

  const prev = useCallback(() => {
    setDirection(-1);
    setActiveIndex((i) => (i - 1 + COUNT) % COUNT);
  }, []);

  const goTo = (target) => {
    if (target === activeIndex) return;
    setDirection(target > activeIndex ? 1 : -1);
    setActiveIndex(target);
  };

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(next, AUTOPLAY_DELAY);
    return () => clearInterval(timer);
  }, [isPaused, next]);

  return (
    <div>
      <ScreenHeading
        title={'Recommendations'}
        subHeading={'What Others Say About Me'}
      />
      <section
        className="testi-section fade-in"
        id={props.id || ''}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onTouchStart={(e) => {
          touchStartX.current = e.touches[0].clientX;
        }}
        onTouchEnd={(e) => {
          const diff = touchStartX.current - e.changedTouches[0].clientX;
          if (diff > 50) next();
          else if (diff < -50) prev();
        }}
      >
        {/* ── Left: photo coverflow (40%) ── */}
        <div className="testi-photo-panel">
          <div className="testi-photo-track">
            {testimonials.map((t, i) => {
              const offset = wrapOffset(i - activeIndex, COUNT);
              const isActive = offset === 0;
              return (
                <motion.div
                  key={t.name}
                  className="testi-photo-slot"
                  style={{ zIndex: isActive ? 2 : 1 }}
                  onClick={() => !isActive && goTo(i)}
                  animate={{
                    x: offset * 130,
                    scale: isActive ? 1 : 0.7,
                    opacity: Math.abs(offset) > 1 ? 0 : 1,
                    filter: isActive
                      ? 'contrast(1) blur(0px)'
                      : 'contrast(0.45) blur(2px)',
                  }}
                  transition={{ type: 'spring', bounce: 0.12, duration: 0.85 }}
                >
                  <img src={t.photo} alt={t.name} draggable={false} />
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* ── Right: quote panel (60%) ── */}
        <div className="testi-quote-panel">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeIndex}
              className="testi-quote-inner"
              initial={{ opacity: 0, y: direction * 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: direction * -40 }}
              transition={{ duration: 0.42, ease: 'easeOut' }}
            >
              <i
                className="fa fa-quote-left testi-quote-icon"
                aria-hidden="true"
              />
              <p className="testi-quote-text">
                {testimonials[activeIndex].quote}
              </p>
              <div className="testi-author-name">
                — {testimonials[activeIndex].name}
              </div>
              <div className="testi-author-role">
                {testimonials[activeIndex].role}
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="testi-dots">
            {testimonials.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`View testimonial ${i + 1}`}
                onClick={() => goTo(i)}
                className={`testi-dot ${
                  i === activeIndex ? 'testi-dot--active' : ''
                }`}
              />
            ))}
          </div>
        </div>
      </section>
      <Copyright />
    </div>
  );
}
